// src/pages/AdminPage.js — settings hub.
// Operations / Inventory / PDI / Contacts / Analytics live in the main
// AppSidebar (see src/components/AppSidebar.js). This page is settings-only:
// notifications, users, databases, schema, api keys, dev tools, import.
// Theme toggle lives in the app sidebar footer.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../scripts/api";
import {
  Upload, Table2Icon, UserCircle, DatabaseIcon, Wrench, Settings2,
  KeyRound, RefreshCw, Bell, Sparkles, MapPin, FileText,
} from "lucide-react";

// ── Settings nav (settings-only — everything else is in the main sidebar) ──
const ADMIN_NAV = [
  {
    label: "General",
    color: "#3b82f6",
    items: [
      { to: "/admin/notifications",       label: "Notifications",     icon: Bell        },
      { to: "/admin/destinations",        label: "Destinations",      icon: MapPin      },
      { to: "/admin/driver-documents",    label: "Driver Documents",  icon: FileText    },
      { to: "/admin/inventory-settings",  label: "Inventory Settings", icon: Settings2   },
    ],
  },
  {
    label: "Access",
    color: "#8b5cf6",
    items: [
      { to: "/admin/users",    label: "Users",    icon: UserCircle },
      { to: "/admin/api-keys", label: "API Keys", icon: KeyRound   },
    ],
  },
  {
    label: "Data",
    color: "#0d9488",
    items: [
      { to: "/admin/dbs",         label: "Databases",   icon: DatabaseIcon },
      { to: "/admin/schema",      label: "Schema",      icon: Table2Icon   },
      { to: "/admin/import",      label: "Import",      icon: Upload       },
      { to: "/admin/master-sync", label: "Master Sync", icon: RefreshCw    },
      { to: "/admin/pg-preview",  label: "PG Preview",  icon: Table2Icon   },
    ],
  },
  {
    label: "Developer",
    color: "#6b7280",
    items: [
      { to: "/admin/devtools", label: "Dev Tools", icon: Wrench },
    ],
  },
];

const COMING_SOON = [
  { label: "Sales Brochure Generator",    sub: "AI-generated brochures for any unit"            },
  { label: "CRM Integration",             sub: "Push production & delivery updates to your CRM" },
  { label: "Social Media Integration",    sub: "Auto-post on delivery & milestones"              },
  { label: "Service Dept. Integration",   sub: "Connect IMS with your service department"        },
  { label: "Quoting Integration",         sub: "Generate quotes from inventory in one click"     },
  { label: "AI Assistant",                sub: "Natural language inventory queries & insights"    },
];

const STATUS_META = {
  available: { color: "#22c55e", label: "Available" },
  on_move:   { color: "#3b82f6", label: "On Move"   },
  off_duty:  { color: "#6b7280", label: "Off Duty"  },
};

// ── Live metrics strip (kept from original — still a nice quick view) ─────
function MetricsStrip() {
  const [drivers,     setDrivers]     = useState([]);
  const [activeMoves, setActiveMoves] = useState(0);
  const [loading,     setLoading]     = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, mRes] = await Promise.all([
        api.get("/driver").catch(() => ({ data: [] })),
        api.get("/moves").catch(() => ({ data: [] })),
      ]);
      setDrivers(Array.isArray(dRes.data) ? dRes.data : []);
      const moves = Array.isArray(mRes.data) ? mRes.data : [];
      setActiveMoves(moves.filter(m => !m.completed_at).length);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const available = drivers.filter(d => (d.driver_status || "available") === "available").length;
  const onMove    = drivers.filter(d => d.driver_status === "on_move").length;
  const offDuty   = drivers.filter(d => d.driver_status === "off_duty").length;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      padding: "10px 14px",
      background: "var(--card-bg)",
      border: "1px solid var(--card-border)",
      borderRadius: 12,
      marginBottom: 16,
    }}>
      <Metric value={activeMoves} label="Active Moves" color="#3b82f6" loading={loading} />
      <div style={{ height: 32, width: 1, background: "var(--card-border)", margin: "0 4px" }} />
      <Metric value={available} label="Available" color="#22c55e" loading={loading} />
      <Metric value={onMove}    label="On Move"   color="#3b82f6" loading={loading} />
      {offDuty > 0 && <Metric value={offDuty} label="Off Duty" color="#6b7280" loading={loading} />}

      {drivers.length > 0 && (
        <>
          <div style={{ height: 32, width: 1, background: "var(--card-border)", margin: "0 4px" }} />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {drivers.map(d => {
              const s   = d.driver_status || "available";
              const col = STATUS_META[s]?.color || "#6b7280";
              return (
                <div key={d.DriverId} style={{
                  display: "flex", alignItems: "center", gap: 5,
                  fontSize: 12, padding: "3px 8px",
                  borderRadius: 99, border: `1px solid ${col}44`,
                  background: `${col}14`, color: "var(--fg)",
                }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: col, flexShrink: 0 }} />
                  <span style={{ fontWeight: 700 }}>{d.FullName?.split(" ")[0]}</span>
                  <span style={{ opacity: 0.6, fontSize: 10 }}>{STATUS_META[s]?.label || s}</span>
                  {d.current_location && (
                    <span style={{ opacity: 0.5, fontSize: 10 }}>· {d.current_location}</span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <button
        onClick={load}
        style={{
          marginLeft: "auto", background: "none", border: "none",
          cursor: "pointer", color: "var(--muted)", padding: 4,
          display: "flex", alignItems: "center",
        }}
        title="Refresh"
      >
        <RefreshCw size={13} style={loading ? { animation: "spin 1s linear infinite" } : {}} />
      </button>
    </div>
  );
}

function Metric({ value, label, color, loading }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 6, padding: "0 6px" }}>
      <span style={{
        fontSize: 22, fontWeight: 900, lineHeight: 1,
        color: loading ? "var(--muted)" : color,
        minWidth: 24, textAlign: "center",
      }}>
        {loading ? "—" : value}
      </span>
      <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.6, whiteSpace: "nowrap", color: "var(--fg)" }}>{label}</span>
    </div>
  );
}

// ── Settings card tile ────────────────────────────────────────────────────
function SettingsCard({ item, groupColor, onClick }) {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      style={{
        background: "var(--card-bg)",
        border: "1px solid var(--card-border)",
        borderRadius: 12,
        padding: "14px 16px",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 12,
        transition: "transform 100ms, box-shadow 100ms, border-color 100ms",
        color: "var(--fg)",
        width: "100%",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = `0 6px 20px rgba(0,0,0,0.12)`;
        e.currentTarget.style.borderColor = groupColor + "66";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.borderColor = "";
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 9, flexShrink: 0,
        background: groupColor + "18",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={17} style={{ color: groupColor }} />
      </div>
      <span style={{ fontWeight: 700, fontSize: 14 }}>{item.label}</span>
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function AdminPage() {
  const nav = useNavigate();

  return (
    <div className="container" style={{ paddingBottom: 56 }}>
      <MetricsStrip />

      <div style={{ fontWeight: 900, fontSize: 22, letterSpacing: -0.3, marginBottom: 4 }}>
        Settings
      </div>
      <div style={{ fontSize: 13, opacity: 0.55, marginBottom: 20 }}>
        System configuration — notifications, users, data, and developer tools.
        Operations, inventory, and PDI live in the main sidebar.
      </div>

      {ADMIN_NAV.map((group) => (
        <div key={group.label} style={{ marginBottom: 24 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: 0.5, color: group.color, opacity: 0.85,
            marginBottom: 10, display: "flex", alignItems: "center", gap: 6,
          }}>
            <div style={{ width: 3, height: 14, borderRadius: 2, background: group.color }} />
            {group.label}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            {group.items.map((item) => (
              <SettingsCard
                key={item.to}
                item={item}
                groupColor={group.color}
                onClick={() => nav(item.to)}
              />
            ))}
          </div>
        </div>
      ))}

      <div style={{
        fontSize: 11, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: 0.5, opacity: 0.4, marginBottom: 10, marginTop: 24,
        display: "flex", alignItems: "center", gap: 6,
      }}>
        <Sparkles size={12} />
        Coming Soon
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
        {COMING_SOON.map((t) => (
          <div key={t.label} style={{
            background: "var(--card-bg)",
            border: "1px solid var(--card-border)",
            borderRadius: 12,
            padding: "12px 14px",
            opacity: 0.45,
            cursor: "not-allowed",
          }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{t.label}</div>
            <div style={{ fontSize: 11, lineHeight: 1.4, opacity: 0.8 }}>{t.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
